import { Table as ReactTable } from '@tanstack/react-table';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { useDebounceValue } from 'noddi-util';
import { ReactNode, useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import type { FilterControl } from '../../../../types/filterControl';
import { NoddiPaginationState } from '../../../../types/pagination';
import { TableQuickFilter } from '../../../../types/table';
import { NoddiInput } from '../../../Inputs/NoddiInput';
import { NoddiDateRangePicker } from '../../../Overlay/Date/NoddiDateRangePicker';
import { getActiveControlsCount } from '../helper';
import { TableFilterTranslations, TableViewTranslations } from '../tableTranslationTypes';
import { FilterToggleButton } from './FilterSidebar/FilterToggleButton';
import { QuickFilterTray } from './QuickFilterTray';
import { ViewToggle } from './ViewToggle';

interface TableFilterHeaderProps<TData, TQuickFilterValue extends string> {
  table: ReactTable<TData>;
  searchPlaceholder: string;
  setPagination: (pagination: NoddiPaginationState) => void;
  pagination: NoddiPaginationState;
  controls: FilterControl[];
  quickFilter?: TableQuickFilter<TQuickFilterValue>;
  setDateRange?: (dateRange: DateRange | undefined) => void;
  dateRange: DateRange | undefined;
  dateRangePlaceholder?: string;
  debounceSearchValueMilliseconds?: number;
  customNode?: ReactNode;
  hideSearch?: boolean;
  filterSidebarOpen?: boolean;
  setFilterSidebarOpen?: (open: boolean) => void;
  translations?: {
    filter?: Partial<TableFilterTranslations>;
    view?: Partial<TableViewTranslations>;
  };
  rowSelectionActionButtons?: ReactNode;
}

export function TableFilterHeader<TData, TQuickFilterValue extends string>({
  table,
  pagination,
  searchPlaceholder,
  setPagination,
  setDateRange,
  dateRange,
  dateRangePlaceholder,
  controls,
  quickFilter,
  debounceSearchValueMilliseconds = 300,
  customNode,
  hideSearch,
  filterSidebarOpen,
  setFilterSidebarOpen,
  translations,
  rowSelectionActionButtons
}: TableFilterHeaderProps<TData, TQuickFilterValue>) {
  const [searchValue, setSearchValue] = useState(pagination.search || '');

  const debouncedSearchValue = useDebounceValue({
    value: searchValue,
    delay: debounceSearchValueMilliseconds
  });

  useEffect(() => {
    if (debouncedSearchValue !== pagination.search) {
      setPagination({ ...pagination, search: debouncedSearchValue, pageIndex: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy: reason not recorded; omits the pagination object; pushes the debounced search into pagination and resets pageIndex to 0
  }, [debouncedSearchValue, pagination.search, setPagination]);

  const activeFiltersCount = getActiveControlsCount(controls);

  return (
    <div className='flex shrink-0 flex-wrap-reverse justify-between gap-2'>
      <div className='flex flex-wrap-reverse gap-2'>
        {controls.length > 0 && (
          <FilterToggleButton
            activeFiltersCount={activeFiltersCount}
            filterSidebarOpen={filterSidebarOpen}
            setFilterSidebarOpen={setFilterSidebarOpen}
            translations={translations?.filter}
          />
        )}
        {!hideSearch && (
          <NoddiInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className='min-w-70'
            startAdornment={
              <NoddiIcon name='Search' size='small' className={cn(!searchValue && 'text-muted-foreground')} />
            }
          />
        )}
        {customNode}
        {(setDateRange || dateRange) && (
          <NoddiDateRangePicker
            dateRange={dateRange}
            placeholder={dateRangePlaceholder}
            showClearButton
            onDateChange={(date) => {
              setPagination({ ...pagination, pageIndex: 0 });
              setDateRange?.(date);
            }}
          />
        )}
      </div>
      {/* min-w-0: a quick-filter tray wider than a phone scrolls inside this row, not the page. */}
      <div className='flex max-w-full min-w-0 items-center gap-2'>
        {quickFilter && (
          <QuickFilterTray
            quickFilter={{
              ...quickFilter,
              // A new scope has its own row count, so page 4 of the old one is usually past its end.
              onChange: (value) => {
                setPagination({ ...pagination, pageIndex: 0 });
                quickFilter.onChange(value);
              }
            }}
          />
        )}
        {rowSelectionActionButtons}
        <ViewToggle table={table} translations={{ view: translations?.view }} />
      </div>
    </div>
  );
}
